<div class="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">

  <!-- ── Barra superior ─────────────────────────────────────────── -->
  <div class="flex flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
    <a routerLink="/automation/workflows"
       class="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:hover:text-gray-300">
      <i class="fa-solid fa-arrow-left"></i>
      <span class="max-w-[16rem] truncate">{{ workflow()?.name ?? 'Workflow' }}</span>
    </a>

    <div class="h-5 w-px bg-gray-200 dark:bg-gray-700"></div>

    @if (workflow(); as wf) {
      <span class="rounded-full px-2 py-0.5 text-xs font-medium"
            [ngClass]="{
              'bg-green-100 text-green-700': wf.status === 'ACTIVE',
              'bg-yellow-100 text-yellow-700': wf.status === 'PAUSED',
              'bg-gray-100 text-gray-600': wf.status === 'DRAFT',
              'bg-red-100 text-red-700': wf.status === 'ARCHIVED'
            }">
        {{ workflowStatusText(wf.status) }}
      </span>
    }

    <!-- Indicador de guardado -->
    @if (saveLabel()) {
      <span class="inline-flex items-center gap-1.5 text-xs"
            [ngClass]="{
              'text-gray-400': store.saveState() === 'saved' || store.saveState() === 'idle',
              'text-amber-600 dark:text-amber-400': store.saveState() === 'unsaved',
              'text-brand-600 dark:text-brand-400': store.saveState() === 'saving',
              'text-rose-600 dark:text-rose-400': store.saveState() === 'error'
            }">
        @if (store.saveState() === 'saving') {
          <i class="fa-solid fa-spinner animate-spin"></i>
        } @else if (store.saveState() === 'saved') {
          <i class="fa-solid fa-check"></i>
        }
        {{ saveLabel() }}
      </span>
    }

    <div class="flex-1"></div>

    <!-- Deshacer / Rehacer -->
    <div class="flex items-center rounded-lg border border-gray-200 dark:border-gray-600">
      <button type="button" (click)="undo()" [disabled]="readOnly() || !store.canUndo()"
              title="Deshacer (Ctrl+Z)"
              class="px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700">
        <i class="fa-solid fa-rotate-left"></i>
      </button>
      <span class="h-5 w-px bg-gray-200 dark:bg-gray-600"></span>
      <button type="button" (click)="redo()" [disabled]="readOnly() || !store.canRedo()"
              title="Rehacer (Ctrl+Shift+Z)"
              class="px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700">
        <i class="fa-solid fa-rotate-right"></i>
      </button>
    </div>

    <!-- Probar (ruta dedicada) -->
    <a [routerLink]="['/automation/workflows', workflowId(), 'testing']"
       class="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 px-3 py-1.5 text-sm text-indigo-700 transition-colors hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-900/20">
      <i class="fa-solid fa-flask"></i> Probar
    </a>

    <!-- Validar: guarda el resultado en el workflow, el backend exige workflows:EDIT -->
    <button *appHasPermission="'workflows:edit'" type="button" (click)="validate()" [disabled]="editor.validating() || loading()"
            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
      @if (editor.validating()) {
        <i class="fa-solid fa-spinner animate-spin"></i>
      } @else {
        <i class="fa-solid fa-circle-check"></i>
      }
      Validar
    </button>

    <!-- Guardar -->
    @if (!readOnly()) {
      <button type="button" (click)="save()" [disabled]="!store.dirty() || editor.saving()"
              class="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-brand-700 disabled:opacity-40">
        @if (editor.saving()) {
          <i class="fa-solid fa-spinner animate-spin"></i>
        } @else {
          <i class="fa-solid fa-floppy-disk"></i>
        }
        Guardar
      </button>
    }
  </div>

  <!-- ── Aviso de solo lectura ──────────────────────────────────── -->
  @if (readOnlyMessage()) {
    <div class="flex flex-shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
      <i class="fa-solid fa-triangle-exclamation shrink-0"></i>
      {{ readOnlyMessage() }}
    </div>
  }

  <!-- ── Avisos de validación a nivel de grafo ──────────────────── -->
  @if (graphErrors().length) {
    <div class="flex-shrink-0 border-b border-rose-200 bg-rose-50 px-4 py-2 dark:border-rose-800 dark:bg-rose-900/20">
      <p class="mb-0.5 text-sm font-medium text-rose-700 dark:text-rose-400">
        {{ graphErrors().length }} aviso(s) de estructura del flujo:
      </p>
      <ul class="space-y-0.5 text-xs text-rose-600 dark:text-rose-400">
        @for (err of graphErrors().slice(0, 3); track err.code) {
          <li>• {{ err.message }}</li>
        }
        @if (graphErrors().length > 3) {
          <li class="text-rose-500">… y {{ graphErrors().length - 3 }} más</li>
        }
      </ul>
    </div>
  }

  <!-- ── Cuerpo ─────────────────────────────────────────────────── -->
  @if (fatalError()) {
    <div class="flex flex-1 flex-col items-center justify-center gap-3 text-center">
      <i class="fa-solid fa-circle-exclamation text-3xl text-rose-400"></i>
      <p class="max-w-md text-sm text-gray-600 dark:text-gray-300">{{ fatalError() }}</p>
      <button type="button" (click)="retry()"
              class="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700">
        Reintentar
      </button>
    </div>
  } @else if (loading()) {
    <div class="flex flex-1 items-center justify-center text-gray-400">
      <i class="fa-solid fa-spinner mr-3 animate-spin text-xl"></i>
      Cargando el constructor…
    </div>
  } @else {
    <div class="flex min-h-0 flex-1">
      <div class="w-56 shrink-0">
        <app-workflow-node-palette />
      </div>

      <div class="min-w-0 flex-1 bg-gray-50 dark:bg-gray-950">
        <app-workflow-flow-canvas [readOnly]="readOnly()" />
      </div>

      <div class="shrink-0">
        <app-workflow-inspector-host />
      </div>
    </div>

    <!-- ── Barra de validación / activación ────────────────────────── -->
    <div class="flex-shrink-0">
      <app-workflow-validation-bar (focusNode)="onFocusNode($event)" />
    </div>
  }
</div>
